/**
 * **Resistance at the non-attack damage sites** (`projects/resistance-on-non-attack-damage.md`,
 * `decisions/0171`). The attack spine (`resolve/damageApply.ts`) reads {@link foldedResistance}
 * gated by Shredded's `ignoreTargetArmor`; every damage path that is NOT an attack reads the same
 * pair through {@link nonAttackResistant}, so a Resistance grant halves a bulldoze run-over, a mine
 * burst, a Trample hit or a Jockey Menu Damage option exactly as it halves a weapon hit. The sites
 * that raise the damage packet (`resolve/damagePacket.ts`: mines, bulldoze, the meltdown, terrain,
 * Jockey, the `damage` atom, heat-as-energy) read these helpers through it, with the packet's
 * `source` as the `attackerId` below.
 *
 * **The `attackerId` each site binds is the real source of the damage, never the target.** That
 * is what decides whether an attacker-relative grant (Point-Defense Shield's `is_closest_hostile`)
 * can fold at that site. An attack-gated grant (Siege Armor) needs `fromAttack` as well, which
 * only the rider and a protector's share set:
 *
 * | site | `attackerId` | attacker-relative grants | attack-gated grants |
 * |---|---|---|---|
 * | `bulldoze.ts` run-over | the rig (the mover) | fold | no |
 * | `mine.ts` detonation | the mine's `deployedBy`, or `null` for a GM-placed mine | fold (a GM-placed mine: unconditional only) | no |
 * | `referee/refereeIdentityAction.ts` Jockey Menu Damage | the jockeying pilot (the actor) | fold | no |
 * | `effects/interpret.ts` `damage` atom (Trample) | `ctx.actors.attacker ?? ctx.actors.self` (the mover), else `null` | fold | no |
 * | `effects/interpret.ts` `extra_damage` rider | `ctx.actors.attacker ?? ctx.actors.self`, else `null` | fold | fold when fired off an attack event |
 * | `meltdown.ts` reactor meltdown blast | the melting-down mech | fold | no |
 * | `tables.ts` heat-as-energy | whoever dealt the heat (an `add_heat` atom's carrier, the attacker, the jockey), else `null` | fold | no |
 * | `resolve/dangerousTerrain.ts` (dangerous terrain) | `null` — terrain has no source | no; unconditional grants fold | no |
 * | `resolve/burn.ts` (the end-of-turn burn tick) | `null` — the tick has no source | no; unconditional grants fold | no |
 * | `referee/refereeGmDamage.ts` (GM damage) | `null` — the intent names no source | no; unconditional grants fold | no |
 * | `resolve/damageApply.ts` a protector's split or redirected share | the attacker | fold | fold (`fromAttack`) |
 *
 * **Only the `extra_damage` rider and a protector's share count as damage from an attack**
 * (`fromAttack`, set when the rider fires off an attack event, and always for the share). Every other site here is not an attack, so a grant gated to
 * attacks (Siege Armor's `match: {}`, "damage from attacks") never folds there even when the source
 * is bound: a mine whose deployer stands 5 away, or a run-over whose rig ends 4+ away, is not
 * halved by it. Point-Defense Shield ("all damage from the closest hostile character") has no such
 * gate and still folds at every sourced site.
 *
 * **`null` is the no-attacker mode** ({@link import("./seed").FoldedResistanceArgs.attackerId}):
 * the predicate context binds no `attacker`, so every attacker-relative predicate reads `false`.
 * Point-Defense Shield and Siege Armor never fold there; Argonaut Shield, Stampede Defense and a
 * flat `resistance: ["burn"]` do. A site passes `null` only when no source character exists —
 * never as a stand-in for one it could name.
 *
 * **Shredded gates it shut** (Core p.78: "don't benefit from ARMOR or RESISTANCE"), the same gate
 * the attack spine applies with `!ignoreTargetArmor &&`. The attack spine reads that bit off its
 * target-side attack fold; a non-attack site has no such fold, so this one folds the target's
 * composed modifiers for it.
 *
 * **Shredded zeroes Armor too** (`projects/shredded-armor-at-non-attack-sites.md`): every site that
 * hands `mitigate` a real Armor rating passes it through {@link nonAttackArmor}, the non-attack twin
 * of the attack spine's `effectiveTargetArmor`. It reads the same Shredded fold as
 * {@link nonAttackResistant}. The sites that never apply Armor are untouched:
 *
 * | site | Armor |
 * |---|---|
 * | `bulldoze.ts` run-over, `mine.ts` detonation, `meltdown.ts` blast, Jockey Menu Damage, GM damage, dangerous terrain, the `damage` atom, heat-as-energy | {@link nonAttackArmor} |
 * | `effects/interpret.ts` `extra_damage` rider | what the hit's own damage left against the attack's target (BC-019), else {@link nonAttackArmor}; none for `bonusDamage` with no hit in scope |
 * | `resolve/damageApply.ts` a protector's share | {@link nonAttackArmor}, spent once across the packet (BC-071) |
 * | `resolve/burn.ts` burn tick | never applied (burn ignores Armor, Core p.66; the tick calls no `mitigate`) |
 *
 * `mitigate` still drops Armor for AP and for burn/heat on its own, so a Shredded target and an AP
 * hit compose to the same 0.
 *
 * **Exposed doubles physical damage at the packet sites** ({@link nonAttackExposed}, Core p.77),
 * read off the same target-side fold as Shredded (BC-068), GM damage, the `extra_damage` rider and
 * Monstrosity Dismemberment included (BC-069).
 *
 * **Damage Immunity is read at every one of these sites too** ({@link nonAttackImmune},
 * `projects/pack-entry-rules-fixes.md` 2.C1). RAW p.68: an immune character "never takes damage,
 * even 0 damage". An immune target takes no Overshield drain, `hp_delta` or cascade step and the
 * site adds an IMMUNE warning, but still reports the hit (`DamageReport.final: 0`), so an `on:"damage"`
 * clause such as Personal Shield's dispersal fires, as it does on the attack path.
 */
import type { DamageType, EncounterState } from "@lancer-vtt/domain";
import { gatherModifiersAt, type LoadedOverlay } from "./loader";
import { foldModifiers } from "./modifiers";
import type { PredicateContext } from "./predicate";
import { damageGrantCaps, foldedResistance, immuneToDamage, SEED_OVERLAY } from "./seed";

/** Args for {@link nonAttackResistant} (max-params-3 discipline, `CODE_STYLE.md`). */
export type NonAttackResistantArgs = {
  state: EncounterState;
  targetId: string;
  /** The real source of the damage — see this module's table. Never `targetId`; `null` only when no source character exists. */
  attackerId: string | null;
  /**
   * Is this damage delivered inside an attack's resolution? The `extra_damage` rider sets it when it
   * fires off an attack event (Cluster Munitions), and a protector's share always does; every other
   * site in this module's table is not an attack and leaves it unset. See {@link import("./seed").FoldedResistanceArgs.fromAttack}.
   */
  fromAttack?: boolean;
  dtype: DamageType;
  overlay: LoadedOverlay | undefined;
};

/** Does `targetId` resist this non-attack damage instance? {@link foldedResistance}, shut by Shredded. */
export function nonAttackResistant(args: NonAttackResistantArgs): boolean {
  const { state, targetId, attackerId, fromAttack = false, dtype, overlay = SEED_OVERLAY } = args;
  if (shredded({ state, targetId, attackerId, overlay })) return false;
  return foldedResistance({ state, targetId, attackerId, fromAttack, dtype, overlay });
}

/** Args for {@link nonAttackGrantCaps}: the instance's {@link nonAttackResistant} read, and what it applied. */
export type NonAttackGrantCapsArgs = NonAttackResistantArgs & { resisted: boolean; immune: boolean };

/**
 * The capped Resistance or Immunity an instance used, for its packet to spend once (`event-engine-damage`
 * R7, BC-077): {@link damageGrantCaps} for the grant that applied, none when neither did.
 */
export function nonAttackGrantCaps(args: NonAttackGrantCapsArgs): { carrier: string; key: string }[] {
  const { resisted, immune, fromAttack = false, ...read } = args;
  if (immune) return damageGrantCaps({ ...read, fromAttack, grant: "immunity" });
  return resisted ? damageGrantCaps({ ...read, fromAttack, grant: "resistance" }) : [];
}

/** Args for {@link nonAttackImmune}: the target, the damage type, the overlay. Shredded does not gate Immunity. */
export type NonAttackImmuneArgs = Omit<NonAttackResistantArgs, "attackerId" | "fromAttack">;

/** Is `targetId` immune to this non-attack damage instance? {@link immuneToDamage}, the attack path's own read. */
export function nonAttackImmune(args: NonAttackImmuneArgs): boolean {
  const { state, targetId, dtype, overlay = SEED_OVERLAY } = args;
  return immuneToDamage({ state, targetId, dtype, overlay });
}

/** The play-by-play line a non-attack site adds when {@link nonAttackImmune} nullifies its damage. */
export function nonAttackImmuneWarning(name: string, dtype: DamageType): string {
  return `${name}: IMMUNE to ${dtype} damage — no effect (RAW p.68).`;
}

/** Args for {@link nonAttackArmor}: {@link NonAttackResistantArgs} without `dtype`, plus the site's Armor rating. */
export type NonAttackArmorArgs = Omit<NonAttackResistantArgs, "dtype" | "fromAttack"> & {
  /** The Armor the site would apply without Shredded — normally `target.base.armor`. */
  armor: number;
};

/** The Armor `targetId` benefits from against this non-attack damage instance: `0` when Shredded, else `armor`. */
export function nonAttackArmor(args: NonAttackArmorArgs): number {
  const { state, targetId, attackerId, overlay = SEED_OVERLAY, armor } = args;
  return shredded({ state, targetId, attackerId, overlay }) ? 0 : armor;
}

/** Args for {@link nonAttackExposed}: {@link NonAttackArmorArgs} without the Armor rating. */
export type NonAttackExposedArgs = Omit<NonAttackArmorArgs, "armor">;

/**
 * Does a target-side modifier (Exposed) double the physical damage `targetId` takes? The non-attack
 * twin of the attack fold's `doubleDamage`: "All kinetic, explosive or energy damage taken by
 * EXPOSED characters is doubled" (Core p.77), whatever deals it.
 */
export function nonAttackExposed(args: NonAttackExposedArgs): boolean {
  const { state, targetId, attackerId, overlay = SEED_OVERLAY } = args;
  return targetSideFold({ state, targetId, attackerId, overlay }).doubleDamage;
}

/** True iff a target-side modifier (Shredded) denies `targetId` the benefit of Armor and Resistance. */
function shredded(args: { state: EncounterState; targetId: string; attackerId: string | null; overlay: LoadedOverlay }): boolean {
  return targetSideFold(args).ignoreTargetArmor;
}

/** `targetId`'s composed target-side modifiers, folded with the damage's source bound as the attacker. */
function targetSideFold(args: { state: EncounterState; targetId: string; attackerId: string | null; overlay: LoadedOverlay }) {
  const { state, targetId, attackerId, overlay } = args;
  const ctx: PredicateContext = { state, actors: { self: targetId, target: targetId, attacker: attackerId ?? undefined } };
  return foldModifiers(gatherModifiersAt({ state, carrierId: targetId, overlay }), "target", ctx);
}
